/**
 * PayerFields — bloco "Pagador" com busca no cadastro (person service).
 *
 * CPF/CNPJ é obrigatório; o nome fica sempre à mostra e o resto do cadastro
 * (contato + endereço) abre no expansor. A busca preenche nome, e-mail, telefone
 * e endereço (resolvendo o city_id em cidade/UF).
 *
 * `lockWhenFound` (default true) trava os campos quando a pessoa vem do cadastro
 * — usado no Registrar Pagamento, onde alterar cadastro é em outro lugar. A baixa
 * de título passa false: lá o pagador vem do título e pode ser ajustado.
 */
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { FaSearch, FaSpinner, FaUserCheck, FaUserSlash, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { personApi, PersonAddress } from '../../../services/personApi';
import { geoLocationApi } from '../../../services/geoLocationApi';
import ProvinceCitySelect from '../ProvinceCitySelect';
import TextEdit from '../TextEdit';
import { onlyDigits } from '../../../utils/taxIdUtils';

export type PayerLookup = 'idle' | 'searching' | 'found' | 'notfound';

export interface PayerValue {
    document: string;
    name: string;
    /** uuid do person service → external_id no billing (null quando não cadastrado) */
    personId: string | null;
    email: string;
    phone: string;
    address_street: string;
    address_number: string;
    address_complement: string;
    address_neighborhood: string;
    address_city: string;
    address_state: string;
    address_zip_code: string;
    lookup: PayerLookup;
}

export const emptyPayer = (overrides: Partial<PayerValue> = {}): PayerValue => ({
    document: '', name: '', personId: null, email: '', phone: '',
    address_street: '', address_number: '', address_complement: '',
    address_neighborhood: '', address_city: '', address_state: '', address_zip_code: '',
    lookup: 'idle',
    ...overrides,
});

// mora em utils/taxIdUtils (sem dependências) e é usada aqui dentro; reexportada para não
// quebrar quem já importava daqui
export { onlyDigits };

interface Props {
    value: PayerValue;
    onChange: (next: PayerValue) => void;
    lockWhenFound?: boolean;
    title?: string;
    labelClassName?: string;
    inputClassName?: string;
    idPrefix?: string;
}

const PayerFields: React.FC<Props> = ({
    value, onChange, lockWhenFound = true, title = 'Pagador', idPrefix = 'payer',
}) => {
    const [expanded, setExpanded] = useState(false);

    const patch = (fields: Partial<PayerValue>) => onChange({ ...value, ...fields });

    const busy = value.lookup === 'searching';                      // trava tudo durante a busca
    const locked = lockWhenFound && value.lookup === 'found';       // dado do cadastro
    const disabled = busy || locked;

    const lookup = useCallback(async () => {
        const doc = onlyDigits(value.document);
        if (doc.length !== 11 && doc.length !== 14) {
            toast.warn('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos)');
            return;
        }
        onChange({ ...value, lookup: 'searching' });
        try {
            const found = await personApi.searchByDocument(doc);   // consolidado CPF/CNPJ
            if (!found) {
                onChange({ ...value, personId: null, lookup: 'notfound' });
                return;
            }
            const email = found.contacts?.find(c => /mail/i.test(c.type))?.content || '';
            const phone = found.contacts?.find(c => /(phone|mobile|cel|tel|whats)/i.test(c.type))?.content || '';
            let addr: PersonAddress | undefined;
            try { addr = (await personApi.getAddresses(found.uuid))[0]; } catch { /* sem endereço */ }
            // o endereço traz cityId (numérico); a cidade aponta para a UF → resolve os dois
            const resolved = addr?.cityId ? await geoLocationApi.resolveCity(addr.cityId) : null;
            onChange({
                ...value,
                name: (found.name || '').trim() || value.name,
                personId: found.uuid,
                email: email || value.email,
                phone: phone || value.phone,
                address_zip_code: addr?.zipcode || value.address_zip_code,
                address_street: addr ? [addr.streetType, addr.street].filter(Boolean).join(' ') : value.address_street,
                address_number: addr?.number || value.address_number,
                address_complement: addr?.complement || value.address_complement,
                address_neighborhood: addr?.district || value.address_neighborhood,
                address_city: resolved?.name || value.address_city,
                address_state: resolved?.provinceInitials || value.address_state,
                lookup: 'found',
            });
        } catch {
            onChange({ ...value, personId: null, lookup: 'notfound' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, onChange]);

    return (
        <div>
            {title && <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">{title}</h3>}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <div className="flex gap-2 items-start">
                        <div className="flex-1">
                            <TextEdit
                                id={`${idPrefix}-document`}
                                label="CPF/CNPJ de quem pagou *"
                                value={value.document}
                                disabled={busy}
                                onChange={e => patch({ document: e.target.value, lookup: 'idle', personId: null })}
                                onBlur={lookup}
                            />
                        </div>
                        <button type="button" onClick={lookup} disabled={busy} title="Buscar no cadastro (person)"
                            className="mt-1 px-3 py-2 rounded bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed">
                            {busy ? <FaSpinner className="animate-spin" /> : <FaSearch />}
                        </button>
                    </div>
                </div>
                <div>
                    <TextEdit
                        label="Nome de quem pagou"
                        value={value.name}
                        disabled={disabled}
                        onChange={e => patch({ name: e.target.value })}
                    />
                </div>
            </div>

            {value.lookup === 'found' && (
                <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                    <FaUserCheck /> Encontrado no cadastro (person). Dados vinculados.
                </div>
            )}
            {value.lookup === 'notfound' && (
                <div className="mt-2 text-xs text-yellow-600 flex items-center gap-1">
                    <FaUserSlash /> Não encontrado no cadastro — o pagador será registrado sem vínculo (external_id). Você pode prosseguir.
                </div>
            )}

            <button type="button" onClick={() => setExpanded(v => !v)}
                className="mt-3 text-xs text-blue-600 flex items-center gap-1 hover:underline">
                {expanded ? <FaChevronUp /> : <FaChevronDown />} {expanded ? 'Ocultar' : 'Mais dados do pagador (opcional)'}
            </button>
            {expanded && (
                <div className="grid grid-cols-3 gap-3 mt-3">
                    <div><TextEdit label="E-mail" disabled={disabled} value={value.email} onChange={e => patch({ email: e.target.value })} placeholder=" " /></div>
                    <div><TextEdit label="Telefone" disabled={disabled} value={value.phone} onChange={e => patch({ phone: e.target.value })} placeholder=" " /></div>
                    <div><TextEdit label="CEP" disabled={disabled} value={value.address_zip_code} onChange={e => patch({ address_zip_code: e.target.value })} placeholder=" " /></div>
                    <div className="col-span-2"><TextEdit label="Logradouro" disabled={disabled} value={value.address_street} onChange={e => patch({ address_street: e.target.value })} placeholder=" " /></div>
                    <div><TextEdit label="Número" disabled={disabled} value={value.address_number} onChange={e => patch({ address_number: e.target.value })} placeholder=" " /></div>
                    <div><TextEdit label="Complemento" disabled={disabled} value={value.address_complement} onChange={e => patch({ address_complement: e.target.value })} placeholder=" " /></div>
                    <div><TextEdit label="Bairro" disabled={disabled} value={value.address_neighborhood} onChange={e => patch({ address_neighborhood: e.target.value })} placeholder=" " /></div>
                    {/* Estado → Cidade (geo-location). No payload seguem como string, não como id. */}
                    <ProvinceCitySelect
                        state={value.address_state}
                        city={value.address_city}
                        onChange={({ state, city }) => patch({ address_state: state, address_city: city })}
                        disabled={disabled}
                    />
                </div>
            )}
        </div>
    );
};

export default PayerFields;
